/**
 * Phrase cleanup utilities for Action Center
 * Removes verbose AI language and formats phrases for professional display
 */

/**
 * Cleans up verbose AI language from phrases
 */
export function cleanActionPhrase(phrase: string | null): string {
  if (!phrase) return '';
  
  // Remove verbose AI conversation fragments
  let cleaned = phrase
    // Remove AI starters
    .replace(/^(Great\.|Okay\.|Sure\.|Perfect\.|Absolutely\.|Alright\.|Got it\.)\s*/i, '')
    .replace(/^(I'll|I will|Let me)\s+/i, '')
    
    // Remove verbose email patterns
    .replace(/\bemail\s+(info\s+)?just\s+to\s+confirm[^.]*\.?/gi, 'email info')
    .replace(/\bemail\s+(that|the)\s+(information|info|details?)\s+(over|to\s+you)\s*\.?/gi, 'email info')
    .replace(/\bsend\s+(that|the)\s+(information|info|details?)\s+(over|to\s+you)\s*\.?/gi, 'send info')
    .replace(/\bget\s+(that|the)\s+(information|info|details?)\s+(over\s+to\s+you|to\s+you)\s*\.?/gi, 'send info')
    .replace(/\bget\s+you\s+(that|the)\s+(information|info|details?)\s*\.?/gi, 'send info')
    
    // Remove verbose callback patterns  
    .replace(/\bI'll\s+have\s+(someone|him|her|them)\s+(call\s+you\s+back|give\s+you\s+a\s+call)[^.]*\.?/gi, 'callback scheduled')
    .replace(/\bhave\s+(someone|them|him|her)\s+(give\s+you\s+a\s+call|call\s+you\s+back)[^.]*\.?/gi, 'callback scheduled')
    .replace(/\bsomeone\s+will\s+(call\s+you\s+back|give\s+you\s+a\s+call)[^.]*\.?/gi, 'callback scheduled')
    
    // Remove verbose appointment patterns
    .replace(/\bschedule\s+a\s+time\s+to\s+(meet|talk|discuss)[^.]*\.?/gi, 'meeting scheduled')
    .replace(/\bset\s+up\s+a\s+(meeting|time\s+to\s+meet)[^.]*\.?/gi, 'meeting scheduled')
    
    // Remove follow-up call references (redundant)
    .replace(/\s+and\s+follow[-\s]?up\s+with\s+a\s+(quick\s+)?call\s+(later|tomorrow)[^.]*\.?/gi, '')
    .replace(/\s+follow[-\s]?up\s+with\s+a\s+(quick\s+)?call[^.]*\.?/gi, '')
    .replace(/\s+over\s+and\s+follow[-\s]?up/gi, '')
    
    // Clean up connectors
    .replace(/\s+(and|then)\s+I'll\s+/gi, ' + ')
    .replace(/\bthat\s+over\b/gi, 'info')
    
    // Remove trailing questions/confirmations
    .replace(/[\s,]*\b(okay\?|alright\?|sound\s+good\?|does\s+that\s+work\?)\s*\.?$/gi, '')
    
    .trim();

  // Remove trailing punctuation for consistency
  cleaned = cleaned.replace(/[.,!?]+$/, '');
  
  // Capitalize first letter if not empty
  if (cleaned.length > 0) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  return cleaned;
}

/**
 * Formats action phrases for display with proper business language
 */
export function formatActionForDisplay(type: string, phrase: string | null): string {
  const cleanedPhrase = cleanActionPhrase(phrase);
  
  switch (type) {
    case 'email_provided':
    case 'email_promise':
      return cleanedPhrase || 'Email info requested';
      
    case 'follow_up_call':
    case 'consent_callback':
      return cleanedPhrase || 'Follow-up call requested';
      
    case 'appointment_interest':
    case 'appointment_booked':
      return cleanedPhrase || 'Appointment scheduled';
      
    case 'message_taking':
      return cleanedPhrase || 'Message to relay';
      
    default:
      return cleanedPhrase || 'Action required';
  }
}

/**
 * Extracts time-sensitive information from phrases
 */
export function extractTimeContext(phrase: string | null): string | null {
  if (!phrase) return null;
  
  const lowerPhrase = phrase.toLowerCase();
  
  // Look for specific time mentions
  const timePatterns = [
    { pattern: /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i, priority: 1 },
    { pattern: /\bnext\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i, priority: 1 },
    { pattern: /\btomorrow\s+at\s+(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i, priority: 2 },
    { pattern: /\bthis\s+(morning|afternoon|evening)/i, priority: 3 },
    { pattern: /\bnext\s+(monday|tuesday|wednesday|thursday|friday)/i, priority: 4 },
    { pattern: /\b(\d{1,2}(?::\d{2})?\s*(?:am|pm))/i, priority: 5 }
  ];

  for (const { pattern } of timePatterns.sort((a, b) => a.priority - b.priority)) {
    const match = phrase.match(pattern);
    if (match) {
      return match[0].trim();
    }
  }

  return null;
}

/**
 * Determines if a phrase indicates urgency
 */
export function extractUrgencyLevel(phrase: string | null): 'ASAP' | 'Today' | 'Tomorrow' | null {
  if (!phrase) return null;
  
  const lowerPhrase = phrase.toLowerCase();
  
  if (lowerPhrase.includes('asap') || lowerPhrase.includes('urgent') || 
      lowerPhrase.includes('immediately') || lowerPhrase.includes('right away')) {
    return 'ASAP';
  }
  
  if (lowerPhrase.includes('today') || lowerPhrase.includes('this morning') || 
      lowerPhrase.includes('this afternoon')) {
    return 'Today';
  }
  
  if (lowerPhrase.includes('tomorrow')) {
    return 'Tomorrow';
  }
  
  return null;
}